import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, TrendingUp, Download, Check, X, AlertCircle, Star, Zap, Building, Crown } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [usageStats, setUsageStats] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      icon: Star,
      price: { monthly: 0, yearly: 0 },
      description: 'Perfect for getting started',
      features: [
        '1 project',
        '500 MB storage',
        'Basic templates',
        'Community support',
        'Basic analytics'
      ],
      limitations: [
        'No custom domains',
        'Limited API calls',
        'No priority support'
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      icon: Zap,
      price: { monthly: 13.50, yearly: 150 },
      description: 'For professional developers',
      features: [
        'Unlimited projects',
        '10 GB storage',
        'All templates',
        'Priority email support',
        'Advanced analytics',
        'Custom domains',
        'API access',
        'Collaboration tools'
      ],
      limitations: [],
      recommended: true
    },
    {
      id: 'business',
      name: 'Business',
      icon: Building,
      price: { monthly: 49, yearly: 490 },
      description: 'For growing teams',
      features: [
        'Everything in Pro',
        'Unlimited storage',
        'Team management (up to 10)',
        'Advanced integrations',
        'Custom workflows',
        'Phone support',
        'SLA guarantee',
        'White-label options'
      ],
      limitations: []
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      icon: Crown,
      price: { monthly: 199, yearly: 1990 },
      description: 'For large organizations',
      features: [
        'Everything in Business',
        'Unlimited team members',
        'Dedicated account manager',
        'Custom integrations',
        'On-premise deployment',
        '24/7 phone support',
        'Custom SLA',
        'Advanced security',
        'Compliance tools'
      ],
      limitations: []
    }
  ];

  useEffect(() => {
    if (user) {
      fetchSubscriptionData();
    }
  }, [user]);

  const handleLogout = async () => {
    await supabase?.auth?.signOut();
    navigate('/login');
  };

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch current subscription
      const { data: subscription, error: subError } = await supabase?.from('subscriptions')?.select('*')?.eq('user_id', user?.id)?.single();

      if (subError && subError?.code !== 'PGRST116') {
        throw subError;
      }

      setCurrentSubscription(subscription);

      // Fetch usage stats
      const { data: workspace, error: workspaceError } = await supabase?.from('workspaces')?.select('*')?.eq('user_id', user?.id)?.single();

      if (!workspaceError && workspace) {
        setUsageStats({
          projects: workspace?.project_count || 0,
          storage: workspace?.storage_used || 0,
          teamMembers: workspace?.team_member_count || 0,
          apiCalls: workspace?.api_calls_this_month || 0
        });
      }

      // Mock payment methods (in production, fetch from Stripe)
      setPaymentMethods([
        {
          id: 'pm_1',
          brand: 'visa',
          last4: '4242',
          exp_month: 12,
          exp_year: 2025,
          is_default: true
        }
      ]);

      // Mock invoices (in production, fetch from Stripe)
      setInvoices([
        {
          id: 'inv_1',
          date: '2025-01-01',
          amount: 13.50,
          status: 'paid',
          invoice_pdf: '#'
        }
      ]);

    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async (plan) => {
    if (plan?.id === 'free' && currentSubscription) {
      // Downgrade to free
      if (window.confirm('Are you sure you want to downgrade to the Free plan? This will take effect at the end of your current billing cycle.')) {
        try {
          setLoading(true);
          
          const { error: updateError } = await supabase?.from('subscriptions')?.update({
              status: 'scheduled_cancel',
              cancel_at_period_end: true,
              updated_at: new Date()?.toISOString()
            })?.eq('id', currentSubscription?.id);

          if (updateError) throw updateError;

          setSuccessMessage('Your subscription will be downgraded to Free at the end of the current billing cycle.');
          await fetchSubscriptionData();
        } catch (err) {
          setError(err?.message);
        } finally {
          setLoading(false);
        }
      }
    } else {
      // Navigate to checkout page instead of inline payment
      const currentPlan = getCurrentPlan();
      navigate('/checkout', {
        state: {
          planId: plan?.id,
          planName: plan?.name,
          billingCycle: billingCycle,
          amount: billingCycle === 'monthly' ? plan?.price?.monthly : plan?.price?.yearly,
          isUpgrade: true,
          currentPlanId: currentPlan?.id
        }
      });
    }
  };

  const getCurrentPlan = () => {
    if (!currentSubscription || currentSubscription?.status !== 'active') {
      return plans?.find(p => p?.id === 'free');
    }
    return plans?.find(p => p?.id === currentSubscription?.plan_id) || plans?.find(p => p?.id === 'free');
  };

  const currentPlan = getCurrentPlan();

  if (loading && !currentSubscription) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar 
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          user={user}
          onLogout={handleLogout}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading subscription details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        user={user}
        onLogout={handleLogout}
      />
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
            <p className="text-gray-600 mt-2">Manage your plan, billing, and usage</p>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">Error</p>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
              <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-green-800 font-medium">Success</p>
                <p className="text-green-700 text-sm mt-1">{successMessage}</p>
              </div>
              <button onClick={() => setSuccessMessage(null)} className="text-green-600 hover:text-green-800">
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Current Plan Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Plan</h2>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  {React.createElement(currentPlan?.icon, { className: "w-6 h-6 text-blue-600" })}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{currentPlan?.name}</h3>
                  <p className="text-gray-600 text-sm mt-1">{currentPlan?.description}</p>
                  {currentSubscription && currentSubscription?.status === 'active' && (
                    <div className="mt-3 space-y-1">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Next billing date:</span>{' '}
                        {new Date(currentSubscription.current_period_end)?.toLocaleDateString()}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Amount:</span> $
                        {currentSubscription?.plan_id === 'pro' ? '13.50' : 
                         currentSubscription?.plan_id === 'business' ? '49.00' : 
                         currentSubscription?.plan_id === 'enterprise' ? '199.00' : '0.00'}/month
                      </p>
                    </div>
                  )}
                  {currentSubscription?.cancel_at_period_end && (
                    <p className="text-sm text-orange-600 mt-2 font-medium">
                      ⚠️ Scheduled to downgrade to Free on {new Date(currentSubscription.current_period_end)?.toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              {currentPlan?.id !== 'enterprise' && (
                <Button onClick={() => handlePlanChange(plans?.find(p => p?.id === 'pro'))}>
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Upgrade Plan
                </Button>
              )}
            </div>
          </div>

          {/* Usage Stats */}
          {usageStats && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage This Month</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Projects</p>
                  <p className="text-2xl font-bold text-gray-900">{usageStats?.projects}</p>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${Math.min((usageStats?.projects / (currentPlan?.id === 'free' ? 1 : 100)) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Storage</p>
                  <p className="text-2xl font-bold text-gray-900">{(usageStats?.storage / 1024)?.toFixed(1)} GB</p>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${Math.min((usageStats?.storage / (currentPlan?.id === 'free' ? 500 : 10000)) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Team Members</p>
                  <p className="text-2xl font-bold text-gray-900">{usageStats?.teamMembers}</p>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full" 
                      style={{ width: `${Math.min((usageStats?.teamMembers / (currentPlan?.id === 'free' ? 1 : 10)) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">API Calls</p>
                  <p className="text-2xl font-bold text-gray-900">{usageStats?.apiCalls}</p>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-orange-600 h-2 rounded-full" 
                      style={{ width: `${Math.min((usageStats?.apiCalls / 10000) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Plan Comparison */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Available Plans</h2>
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingCycle === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Yearly
                  <span className="ml-2 text-xs text-green-600 font-semibold">Save 15%</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans?.map((plan) => {
                const PlanIcon = plan?.icon;
                const isCurrentPlan = currentPlan?.id === plan?.id;
                const price = billingCycle === 'monthly' ? plan?.price?.monthly : plan?.price?.yearly;
                const yearlyPrice = plan?.price?.yearly;
                const monthlySavings = billingCycle === 'yearly' ? ((plan?.price?.monthly * 12) - yearlyPrice)?.toFixed(2) : 0;

                return (
                  <div
                    key={plan?.id}
                    className={`relative border-2 rounded-lg p-6 ${
                      plan?.recommended
                        ? 'border-blue-600 shadow-lg'
                        : isCurrentPlan
                        ? 'border-green-500' : 'border-gray-200'
                    }`}
                  >
                    {plan?.recommended && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          Recommended
                        </span>
                      </div>
                    )}
                    {isCurrentPlan && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          Current Plan
                        </span>
                      </div>
                    )}
                    <div className="text-center mb-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <PlanIcon className="w-6 h-6 text-gray-700" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{plan?.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{plan?.description}</p>
                      <div className="mt-4">
                        <span className="text-4xl font-bold text-gray-900">${price}</span>
                        <span className="text-gray-600">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                        {billingCycle === 'yearly' && monthlySavings > 0 && (
                          <p className="text-sm text-green-600 font-medium mt-1">
                            Save ${monthlySavings}/year
                          </p>
                        )}
                      </div>
                    </div>
                    <ul className="space-y-3 mb-6">
                      {plan?.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => !isCurrentPlan && handlePlanChange(plan)}
                      disabled={isCurrentPlan}
                      className="w-full"
                      variant={plan?.recommended ? 'primary' : 'secondary'}
                    >
                      {isCurrentPlan ? 'Current Plan' : plan?.id === 'free' ? 'Downgrade' : 'Upgrade'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Payment Methods */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Payment Methods</h2>
              <Button variant="secondary" size="sm">
                <CreditCard className="w-4 h-4 mr-2" />
                Add Payment Method
              </Button>
            </div>
            <div className="space-y-3">
              {paymentMethods?.map((method) => (
                <div key={method?.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 capitalize">{method?.brand} •••• {method?.last4}</p>
                      <p className="text-sm text-gray-600">Expires {method?.exp_month}/{method?.exp_year}</p>
                    </div>
                  </div>
                  {method?.is_default && (
                    <span className="text-sm text-green-600 font-medium">Default</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Billing History */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Billing History</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Amount</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices?.map((invoice) => (
                    <tr key={invoice?.id} className="border-b border-gray-100">
                      <td className="py-3 px-4 text-sm text-gray-900">
                        {new Date(invoice.date)?.toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900">${invoice?.amount?.toFixed(2)}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          invoice?.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {invoice?.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-flex items-center gap-1">
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;